import type { ComponentType } from '../types';

// Money rendering converged onto the canonical app-wide formatter (ISO code +
// grouped digits — "TZS 1,500,000" / compact "TZS 1.5M"), so My Pay never
// disagrees with payroll/exports about how TZS looks.
export { formatMoney } from '@/lib/money';

export function formatDate(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

/** "2026-06" → "June 2026" (humanised period label). */
export function humanizePeriod(label: string): string {
  const m = /^(\d{4})-(\d{2})/.exec(label || '');
  if (!m) return label || '';
  const d = new Date(Number(m[1]), Number(m[2]) - 1, 1);
  return d.toLocaleDateString(undefined, { month: 'long', year: 'numeric' });
}

export const MASK = '••••••';

// --- structural taxonomy (NOT country deduction names) --------------------

export type Bucket = 'earnings' | 'deductions' | 'contributions';

interface CategoryMeta {
  /** English fallback (used by the rule-based insights prose). */
  label: string;
  /** i18n key in the payroll namespace — components render t(labelKey). */
  labelKey: string;
  bucket: Bucket;
  color: string;
  sign: '+' | '-';
}

export const CATEGORY_META: Record<ComponentType, CategoryMeta> = {
  EARNING: { label: 'Earnings', labelKey: 'myPay.cat.EARNING', bucket: 'earnings', color: '#16a34a', sign: '+' },
  STATUTORY_TAX: { label: 'Taxes', labelKey: 'myPay.cat.STATUTORY_TAX', bucket: 'deductions', color: '#ef4444', sign: '-' },
  PRE_TAX_DEDUCTION: { label: 'Pre-tax deductions', labelKey: 'myPay.cat.PRE_TAX_DEDUCTION', bucket: 'deductions', color: '#f59e0b', sign: '-' },
  POST_TAX_DEDUCTION: { label: 'Post-tax deductions', labelKey: 'myPay.cat.POST_TAX_DEDUCTION', bucket: 'deductions', color: '#8b5cf6', sign: '-' },
  EMPLOYER_CONTRIBUTION: { label: 'Employer contributions', labelKey: 'myPay.cat.EMPLOYER_CONTRIBUTION', bucket: 'contributions', color: '#3b82f6', sign: '+' },
};

/** Friendly status from the payslip render status (MVP mapping — the badge says
 *  "Paid" once the statement is generated; true disbursement status needs the
 *  payroll self-scoped payment endpoint, see the design doc's Phase 5). */
export function friendlyStatus(s: string): { labelKey: string; tone: 'success' | 'warning' | 'secondary' } {
  switch (s) {
    case 'generated':
      return { labelKey: 'myPay.status.paid', tone: 'success' };
    case 'pending':
      return { labelKey: 'myPay.status.processing', tone: 'warning' };
    default:
      return { labelKey: 'myPay.status.draft', tone: 'secondary' };
  }
}
